import React, { useState, useEffect } from 'react';
import QuizList from './QuizList';
import QuizTopicSelect from './QuizTopicSelect';
import QuizView from './QuizView';
import '../styles/QuizWorkflow.scss';
import { track } from '@vercel/analytics';

function QuizWorkflow({
  API_BASE_URL,
  authToken,
  userData,
  subject,
  onBack,
  initialQuizId,
  initialAttemptId
}) {
  const [workflowStep, setWorkflowStep] = useState('topics'); // Always start with topics since subject is provided
  const [selectedSubject, setSelectedSubject] = useState(subject || null);
  const [quizId, setQuizId] = useState(initialQuizId || null);
  const [attemptId, setAttemptId] = useState(initialAttemptId || null);
  const [isFreshWorkflow, setIsFreshWorkflow] = useState(false); // Track if user started a fresh workflow
  
  // When subject prop changes, update selectedSubject and workflowStep
  useEffect(() => {
    if (subject) {
      setSelectedSubject(subject);
      if (initialQuizId && !isFreshWorkflow) {
        // Coming from Dashboard - go directly to quiz view
        setQuizId(initialQuizId);
        setAttemptId(initialAttemptId);
        setWorkflowStep('quiz');
        
        // Track workflow entry from dashboard
        track('quiz_workflow_entered_from_dashboard', {
          subject: subject,
          quiz_id: initialQuizId,
          attempt_id: initialAttemptId,
          user_role: userData?.role || 'unknown'
        });
      } else {
        // Fresh start - go to topics
        setWorkflowStep('topics');
        
        // Track workflow entry for new quiz
        track('quiz_workflow_entered_fresh', {
          subject: subject,
          user_role: userData?.role || 'unknown'
        });
      }
    }
  }, [subject, initialQuizId, initialAttemptId, isFreshWorkflow, userData?.role]);
  
  // Handle topic selection and quiz creation
  const handleTopicsSelected = async (topics, questionCount) => {
    try {
      // Mark that we're starting a fresh workflow
      setIsFreshWorkflow(true);
      
      // Track quiz generation start
      track('quiz_generation_started', {
        subject: selectedSubject,
        topics: topics,
        topic_count: topics.length,
        question_count: questionCount,
        user_role: userData?.role || 'unknown'
      });
      
      // Create a new quiz with the selected topics
      const response = await fetch(`${API_BASE_URL}/api/quiz/generate`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: selectedSubject,
          topics: topics,
          question_count: questionCount
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to generate quiz: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Poll for quiz generation completion
      const quizData = await pollQuizGeneration(data.task_id);
      
      // When complete, set the quiz ID and move to quiz view
      setQuizId(quizData.quiz_id);
      setWorkflowStep('quiz');
      
      // Track successful quiz generation
      track('quiz_generation_completed', {
        subject: selectedSubject,
        quiz_id: quizData.quiz_id,
        topics: topics,
        topic_count: topics.length,
        question_count: questionCount,
        user_role: userData?.role || 'unknown'
      });
    } catch (error) {
      console.error('Error generating quiz:', error);
      alert('Failed to generate quiz. Please try again.');
      
      // Track quiz generation error
      track('quiz_generation_error', {
        subject: selectedSubject,
        topics: topics,
        topic_count: topics.length,
        question_count: questionCount,
        error: error.message,
        user_role: userData?.role || 'unknown'
      });
    }
  };
  
  // New handler for when a quiz is loaded by Share ID
  const handleShareIdEntered = (loadedQuizId) => {
    // Mark that we're starting a fresh workflow
    setIsFreshWorkflow(true);
    setQuizId(loadedQuizId);
    setAttemptId(null); // Ensure we are starting a new attempt
    setWorkflowStep('quiz');
    
    // Track share ID usage
    track('quiz_loaded_by_share_id', {
      subject: selectedSubject,
      quiz_id: loadedQuizId,
      user_role: userData?.role || 'unknown'
    });
  };
  
  // Poll for quiz generation status
  const pollQuizGeneration = async (taskId) => {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/quiz/status/${taskId}`,
            {
              headers: {
                'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`
              }
            }
          );
          
          if (!response.ok) {
            throw new Error(`Failed to check quiz status: ${response.status}`);
          }
          
          const data = await response.json();
          
          if (data.status === 'completed') {
            resolve(data);
          } else if (data.status === 'failed') {
            reject(new Error('Quiz generation failed'));
          } else {
            // Still processing, check again after delay
            setTimeout(checkStatus, 2000);
          }
        } catch (error) {
          reject(error);
        }
      };
      
      // Start polling
      checkStatus();
    });
  };
  
  // Handle selecting a quiz to take
  const handleSelectQuiz = (id, attemptId = null) => {
    setQuizId(id);
    setAttemptId(attemptId);
    setWorkflowStep('quiz');
  };
  
  // Reset workflow to fresh state
  const resetWorkflow = () => {
    setQuizId(null);
    setAttemptId(null);
    setIsFreshWorkflow(true); // Mark as fresh workflow
    setWorkflowStep('topics'); // Always go to topics since subject is already selected
  };
  
  // Handle quiz completion
  const handleQuizComplete = () => {
    // Reset state and go back to appropriate step
    if (initialQuizId) {
      // If we came from Dashboard, go back to Dashboard
      onBack();
    } else {
      // Otherwise, reset for a fresh workflow
      resetWorkflow();
    }
  };
  
  // Render based on current workflow step
  if (workflowStep === 'topics') {
    return (
      <QuizTopicSelect
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        subject={selectedSubject}
        onTopicsSelected={handleTopicsSelected}
        onShareIdEntered={handleShareIdEntered}
        onBack={() => {
          if (initialQuizId) {
            onBack();
          } else {
            onBack(); // Go back to main category selection
          }
        }}
      />
    );
  }
  
  if (workflowStep === 'list') {
    return (
      <QuizList
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        onSelectQuiz={handleSelectQuiz}
        onCreateQuiz={() => setWorkflowStep('topics')}
        onBack={onBack}
      />
    );
  }
  
  if (workflowStep === 'quiz') {
    return (
      <QuizView
        API_BASE_URL={API_BASE_URL}
        authToken={authToken}
        quizId={quizId}
        attemptId={attemptId}
        onBack={() => {
          if (initialQuizId) {
            // If we came from Dashboard, go back to Dashboard
            onBack();
          } else {
            // Otherwise, reset for a fresh workflow
            resetWorkflow();
          }
        }}
        onComplete={handleQuizComplete}
      />
    );
  }
  
  // Fallback
  return null;
}

export default QuizWorkflow; 